<template>
  <div>
    <div class="page-head">
      <div>
        <h2>服务订单</h2>
        <p>处理咨询付费、服务购买和定制相关订单。</p>
      </div>
    </div>

    <el-card shadow="never">
      <el-form :inline="true" :model="filters" size="small">
        <el-form-item label="关键词"><el-input v-model="filters.keyword" placeholder="客户编号/咨询编号/订单号/名称" clearable /></el-form-item>
        <el-form-item label="订单状态">
          <el-select v-model="filters.status" placeholder="全部" clearable>
            <el-option label="待处理" value="pending" />
            <el-option label="处理中" value="processing" />
            <el-option label="已完成" value="completed" />
            <el-option label="已取消" value="cancelled" />
          </el-select>
        </el-form-item>
        <el-form-item><el-button type="primary" @click="fetchList">查询</el-button><el-button @click="reset">重置</el-button></el-form-item>
      </el-form>

      <paged-table :rows="rows" :loading="loading" :page="page" :page-size="pageSize" :total="total" @page-change="page = $event; fetchList()" @size-change="pageSize = $event; page = 1; fetchList()">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="order_no" label="订单号" width="190" />
        <el-table-column prop="customer_no" label="客户编号" width="205" />
        <el-table-column prop="consultation_no" label="来源咨询" width="205"><template slot-scope="{ row }">{{ row.consultation_no || '-' }}</template></el-table-column>
        <el-table-column prop="name" label="订单名称" min-width="170" />
        <el-table-column prop="order_type" label="类型" width="110">
          <template slot-scope="{ row }">{{ orderTypeText(row.order_type) }}</template>
        </el-table-column>
        <el-table-column prop="order_subtype" label="订单分类" width="110">
          <template slot-scope="{ row }">{{ orderSubtypeText(row.order_subtype) }}</template>
        </el-table-column>
        <el-table-column prop="amount" label="金额" width="110" />
        <el-table-column label="服务需求" min-width="180"><template slot-scope="{ row }">{{ formatNeeds(row.service_needs_json) }}</template></el-table-column>
        <el-table-column prop="hotel_level" label="酒店标准" width="110" />
        <el-table-column prop="payment_status" label="支付状态" width="110"><template slot-scope="{ row }"><status-tag :value="row.payment_status" /></template></el-table-column>
        <el-table-column prop="status" label="订单状态" width="110"><template slot-scope="{ row }"><status-tag :value="row.status" /></template></el-table-column>
        <el-table-column label="下单时间" width="180"><template slot-scope="{ row }">{{ row.created_at | dateTime }}</template></el-table-column>
        <el-table-column label="操作" width="120" fixed="right"><template slot-scope="{ row }"><el-button type="text" @click="openEdit(row)">处理</el-button></template></el-table-column>
      </paged-table>
    </el-card>

    <el-dialog title="处理服务订单" :visible.sync="drawerVisible" width="600px">
      <el-form class="drawer-form" :model="form" label-width="120px">
        <el-form-item label="订单号"><el-input v-model="form.order_no" disabled /></el-form-item>
        <el-form-item label="客户编号"><el-input v-model="form.customer_no" disabled /></el-form-item>
        <el-form-item label="来源咨询"><el-input :value="form.consultation_no || '无直接关联咨询'" disabled /></el-form-item>
        <el-form-item label="订单名称"><el-input v-model="form.name" /></el-form-item>
        <el-form-item label="关联用户"><el-input :value="`${form.user_nickname || '-'}（ID ${form.user_id || '-'}）`" disabled /></el-form-item>
        <el-form-item label="关联产品"><el-input :value="`${form.product_title || form.name || '-'}（ID ${form.product_id || '-'}）`" disabled /></el-form-item>
        <el-form-item label="订单金额"><el-input :value="`¥${form.amount || 0}`" disabled /><div class="field-help">金额为下单时的基础服务价格快照，后台不可修改。</div></el-form-item>
        <el-form-item label="订单分类">
          <el-select v-model="form.order_subtype" clearable>
            <el-option label="基础服务" value="basic" />
            <el-option label="高端定制" value="custom" />
          </el-select>
        </el-form-item>
        <el-form-item label="用户侧状态">
          <el-select v-model="form.user_status" @change="syncOrderStatusFromUserStatus">
            <el-option label="待出行" value="pending_trip" />
            <el-option label="出行中" value="traveling" />
            <el-option label="已完成" value="completed" />
            <el-option label="已退款" value="refunded" />
          </el-select>
        </el-form-item>
        <el-form-item label="出发日期"><el-date-picker v-model="form.travel_date" value-format="yyyy-MM-dd" type="date" /></el-form-item>
        <el-form-item label="返程日期"><el-date-picker v-model="form.end_date" value-format="yyyy-MM-dd" type="date" /></el-form-item>
        <el-form-item label="服务需求"><el-input :value="formatNeeds(form.service_needs_json)" disabled /></el-form-item>
        <el-form-item label="酒店标准"><el-input v-model="form.hotel_level" /></el-form-item>
        <el-form-item label="成人数"><el-input-number v-model="form.adult_count" :min="0" /></el-form-item>
        <el-form-item label="儿童数"><el-input-number v-model="form.child_count" :min="0" /></el-form-item>
        <el-form-item label="老人数"><el-input-number v-model="form.elder_count" :min="0" /></el-form-item>
        <el-form-item label="旅客信息">
          <el-table v-loading="travelersLoading" :data="travelers" size="mini" border empty-text="暂无旅客信息">
            <el-table-column prop="name" label="姓名" min-width="90" />
            <el-table-column prop="phone" label="手机号（脱敏）" min-width="130" />
            <el-table-column prop="id_number" label="证件号（脱敏）" min-width="170" />
            <el-table-column v-if="canRevealSensitive" label="敏感操作" width="90">
              <template slot-scope="{ row }"><el-button type="text" @click="revealTraveler(row)">授权查看</el-button></template>
            </el-table-column>
          </el-table>
          <div class="field-help">默认仅显示脱敏信息；授权查看必须填写原因并记录审计日志。</div>
        </el-form-item>
        <el-form-item label="服务节点">
          <el-button size="mini" type="primary" @click="addStep">新增节点</el-button>
          <el-table :data="steps" size="mini" border empty-text="暂无服务节点">
            <el-table-column prop="title" label="标题" min-width="130" />
            <el-table-column prop="step_status" label="状态" width="130"><template slot-scope="{row}"><el-select :value="row.step_status" size="mini" @change="updateStep(row,{step_status:$event})"><el-option label="待开始" value="pending"/><el-option label="进行中" value="in_progress"/><el-option label="已完成" value="completed"/><el-option label="已取消" value="cancelled"/></el-select></template></el-table-column>
            <el-table-column prop="sort_order" label="排序" width="100"><template slot-scope="{row}"><el-input-number :value="Number(row.sort_order||0)" size="mini" :min="0" controls-position="right" @change="updateStep(row,{sort_order:$event})"/></template></el-table-column>
            <el-table-column label="操作" width="120"><template slot-scope="{row}"><el-button type="text" @click="editStep(row)">编辑</el-button><el-button type="text" class="danger-text" @click="removeStep(row)">删除</el-button></template></el-table-column>
          </el-table>
        </el-form-item>
        <el-form-item label="支付记录">
          <el-table :data="payments" size="mini" border empty-text="暂无支付记录">
            <el-table-column prop="payment_no" label="支付单号" min-width="180" />
            <el-table-column prop="amount" label="金额" width="90" />
            <el-table-column prop="payment_status" label="状态" width="100" />
            <el-table-column prop="wechat_transaction_id" label="微信交易号" min-width="180" />
          </el-table>
        </el-form-item>
        <el-form-item label="推荐佣金">
          <el-table :data="commissions" size="mini" border empty-text="该订单暂无佣金记录">
            <el-table-column prop="recommender_id" label="推荐官ID" width="100" />
            <el-table-column prop="amount" label="佣金" width="90" />
            <el-table-column label="比例" width="90"><template slot-scope="{row}">{{ row.commission_rate === null ? '-' : `${Number(row.commission_rate) * 100}%` }}</template></el-table-column>
            <el-table-column prop="status" label="状态" width="100" />
            <el-table-column prop="remark" label="说明" min-width="160" />
          </el-table>
        </el-form-item>
        <el-form-item label="电子合同"><asset-upload v-model="form.contract_url" type="file" category="contracts" /></el-form-item>
        <el-form-item label="合同名称"><el-input v-model="form.contract_name" placeholder="不填则按文件名展示" /></el-form-item>
        <el-form-item label="合同上传信息"><el-input :value="fileMeta('contract')" disabled /></el-form-item>
        <el-form-item label="出行险"><asset-upload v-model="form.insurance_url" type="file" category="insurance" /></el-form-item>
        <el-form-item label="保险名称"><el-input v-model="form.insurance_name" placeholder="不填则按文件名展示" /></el-form-item>
        <el-form-item label="保险上传信息"><el-input :value="fileMeta('insurance')" disabled /></el-form-item>
        <el-form-item label="订单状态">
          <el-select v-model="form.status" @change="syncUserStatusFromOrderStatus">
            <el-option label="待处理" value="pending" />
            <el-option label="处理中" value="processing" />
            <el-option label="已完成" value="completed" />
            <el-option label="已取消" value="cancelled" />
          </el-select>
        </el-form-item>
        <el-form-item label="支付状态">
          <el-input :value="form.payment_status" disabled />
          <div class="field-help">支付状态只能由微信支付回调或“支付记录”页面主动查单更新。</div>
        </el-form-item>
        <el-form-item label="负责员工ID"><el-input-number v-model="form.assigned_admin_id" :min="0" /></el-form-item>
        <el-form-item label="推荐官归因"><el-input :value="form.recommender_code ? `${form.recommender_name || '-'}（${form.recommender_code} / ID ${form.recommender_id || '-'}）` : '无'" disabled /></el-form-item>
        <el-form-item label="员工归因"><el-input :value="form.staff_code ? `${form.staff_name || '-'}（${form.staff_code} / ID ${form.staff_admin_id || '-'}）` : '无'" disabled /></el-form-item>
        <el-form-item label="归因来源"><el-input :value="form.attribution_source || '无'" disabled /><div class="field-help">归因信息为下单时校验后的业务快照，后台不可直接修改。</div></el-form-item>
        <el-form-item label="处理备注"><el-input v-model="form.remark" type="textarea" :rows="5" /></el-form-item>
      </el-form>
      <div class="drawer-footer">
        <el-button @click="drawerVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="save">保存订单</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import AssetUpload from "../components/AssetUpload.vue";
import PagedTable from "../components/PagedTable.vue";
import StatusTag from "../components/StatusTag.vue";
import { listResource, updateResource } from "../api/resources";
import { getCurrentUser } from "../api/auth";
import http from "../api/http";

export default {
  name: "ServiceOrdersManage",
  components: { AssetUpload, PagedTable, StatusTag },
  data() {
    return {
      loading: false,
      saving: false,
      drawerVisible: false,
      rows: [],
      total: 0,
      page: 1,
      pageSize: 20,
      filters: { keyword: "", status: "" },
      form: {},
      travelers: [],
      travelersLoading: false,
      currentAdmin: null,
      steps: [],
      payments: [],
      commissions: []
    };
  },
  mounted() {
    this.fetchList();
    this.fetchCurrentAdmin();
  },
  computed: {
    canRevealSensitive() {
      const admin = this.currentAdmin || {};
      return admin.role_code === "super_admin" || (admin.permissions || []).includes("sensitive-data:reveal");
    }
  },
  methods: {
    async fetchCurrentAdmin() {
      try {
        const res = await getCurrentUser();
        this.currentAdmin = res.data && res.data.user ? res.data.user : null;
      } catch (error) {
        this.currentAdmin = null;
      }
    },
    async fetchList() {
      this.loading = true;
      try {
        const res = await listResource("service-orders", { ...this.filters, page: this.page, pageSize: this.pageSize });
        this.rows = res.data && res.data.items ? res.data.items : [];
        this.total = res.data && res.data.total ? res.data.total : 0;
      } finally {
        this.loading = false;
      }
    },
    reset() {
      this.filters = { keyword: "", status: "" };
      this.page = 1;
      this.fetchList();
    },
    orderTypeText(value) {
      const map = {
        consult: "咨询订单",
        service: "服务订单",
        custom: "定制订单",
        route: "线路订单"
      };
      return map[value] || value || "-";
    },
    orderSubtypeText(value) {
      const map = {
        basic: "基础服务",
        custom: "高端定制",
        route: "线路定制"
      };
      return map[value] || value || "-";
    },
    async openEdit(row) {
      this.form = {
        ...row,
        assigned_admin_id: Number(row.assigned_admin_id || 0),
        amount: Number(row.amount || 0),
        travel_date: this.$formatDate(row.travel_date, ""),
        end_date: this.$formatDate(row.end_date, ""),
        adult_count: Number(row.adult_count || 0),
        child_count: Number(row.child_count || 0),
        elder_count: Number(row.elder_count || 0),
        user_status: row.user_status || "pending_trip",
        order_subtype: row.order_subtype || "basic"
      };
      this.drawerVisible = true;
      this.travelers = [];
      this.travelersLoading = true;
      try {
        const res = await http.get(`/service-orders/${row.id}/detail`);
        const detail = res.data || {};
        this.form = {
          ...this.form,
          ...detail,
          travel_date: this.$formatDate(detail.travel_date || this.form.travel_date, ""),
          end_date: this.$formatDate(detail.end_date || this.form.end_date, ""),
          adult_count: Number(detail.adult_count || 0),
          child_count: Number(detail.child_count || 0),
          elder_count: Number(detail.elder_count || 0)
        };
        this.travelers = detail.travelers || [];
        this.steps = detail.steps || [];
        this.payments = detail.payments || [];
        this.commissions = detail.commissions || [];
      } finally {
        this.travelersLoading = false;
      }
    },
    fileMeta(type) {
      const by = this.form[`${type}_uploaded_by`];
      const at = this.form[`${type}_uploaded_at`];
      return by || at ? `上传人ID：${by || '-'}；上传时间：${this.$formatDateTime(at)}` : "尚未记录上传元数据";
    },
    async reloadDetail() {
      const res = await http.get(`/service-orders/${this.form.id}/detail`);
      const detail=res.data||{}; this.steps=detail.steps||[]; this.payments=detail.payments||[]; this.commissions=detail.commissions||[]; this.travelers=detail.travelers||[];
    },
    async addStep() {
      try { const p=await this.$prompt("请输入服务节点标题","新增服务节点",{inputValidator:v=>String(v||"").trim()?true:"标题不能为空"}); await http.post(`/service-orders/${this.form.id}/steps`,{title:p.value,step_status:"pending",sort_order:this.steps.length}); await this.reloadDetail(); } catch(error){ if(error!=="cancel"&&error!=="close"&&error.message)this.$message.error(error.message); }
    },
    async editStep(row) {
      try { const p=await this.$prompt("修改节点标题","编辑服务节点",{inputValue:row.title}); await http.put(`/service-orders/${this.form.id}/steps/${row.id}`,{...row,title:p.value}); await this.reloadDetail(); } catch(error){ if(error!=="cancel"&&error!=="close"&&error.message)this.$message.error(error.message); }
    },
    async updateStep(row, patch) { await http.put(`/service-orders/${this.form.id}/steps/${row.id}`,{...row,...patch}); await this.reloadDetail(); },
    async removeStep(row) { await this.$confirm("确认删除该服务节点？","提示",{type:"warning"}); await http.delete(`/service-orders/${this.form.id}/steps/${row.id}`); await this.reloadDetail(); },
    async revealTraveler(row) {
      try {
        const prompt = await this.$prompt("请填写本次查看完整证件号/手机号的业务原因", "敏感信息授权查看", {
          confirmButtonText: "确认查看",
          cancelButtonText: "取消",
          inputType: "textarea",
          inputValidator: (value) => String(value || "").trim().length >= 4 || "查看原因不少于4个字"
        });
        const res = await http.post(`/sensitive-data/order-travelers/${row.id}/reveal`, { reason: prompt.value });
        const item = res.data || {};
        await this.$alert(`姓名：${item.name || "-"}\n手机号：${item.phone || "-"}\n证件号：${item.id_number || "-"}`, "已授权的旅客信息", {
          confirmButtonText: "关闭"
        });
      } catch (error) {
        if (error !== "cancel" && error !== "close" && error.message) this.$message.error(error.message);
      }
    },
    formatNeeds(value) {
      if (!value) return "-";
      if (Array.isArray(value)) return value.length ? value.join("、") : "-";
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) && parsed.length ? parsed.join("、") : "-";
      } catch (error) {
        return String(value);
      }
    },
    syncOrderStatusFromUserStatus(status) {
      if (status === "completed") this.$set(this.form, "status", "completed");
    },
    syncUserStatusFromOrderStatus(status) {
      if (status === "completed") this.$set(this.form, "user_status", "completed");
    },
    async save() {
      this.saving = true;
      try {
        const res = await updateResource("service-orders", this.form.id, this.form);
        this.form = { ...this.form, ...(res.data || {}) };
        await this.fetchList();
        this.$message.success("订单已保存");
        this.drawerVisible = false;
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.field-help {
  margin-top: 6px;
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
}
</style>
